import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { crmKeys, downloadExport, useBoard, useDeals, useMoveDeal, usePipelines } from "@/api/crm";
import { useMe } from "@/api/hooks";
import type { Deal, Stage } from "@/api/types";
import { StatusBadge } from "@/components/crm/Badge";
import { Icon } from "@/components/icons";
import { Alert, Modal, PageHeader, Spinner } from "@/components/ui";
import { ScoreChip } from "@/features/ai/AiPanels";
import { money, shortDate } from "@/lib/format";
import { can } from "@/lib/permissions";
import { DealForm } from "./DealForm";
import { LostReasonDialog } from "./LostReasonDialog";

function DealCard({ deal, stages, canMove, onMove }: {
  deal: Deal; stages: Stage[]; canMove: boolean; onMove: (dealId: string, stage: Stage) => void;
}) {
  const rotting = deal.status === "open" && deal.days_in_stage > 14;
  return (
    <article draggable={canMove} data-deal-id={deal.id} aria-label={`Deal ${deal.name}`}
      onDragStart={(e) => { e.dataTransfer.setData("text/deal-id", deal.id); e.dataTransfer.effectAllowed = "move"; }}
      className={`rounded-md border border-line bg-surface p-3 shadow-sm transition hover:border-brand/40 hover:shadow ${canMove ? "cursor-grab active:cursor-grabbing" : ""}`}>
      <Link to={`/deals/${deal.id}`} draggable={false} className="block hover:text-brand">
        <p className="text-sm font-medium leading-snug">{deal.name}</p>
        {deal.account && <p className="mt-0.5 truncate text-xs text-muted">{deal.account.name}</p>}
      </Link>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs">
        <span className="font-semibold text-ink">{money(deal.amount, deal.currency)}</span>
        <span className="flex items-center gap-2">
          <ScoreChip score={deal.score} />
          <span className={rotting ? "font-medium text-warn" : "text-muted"} title="Days in this stage">{deal.days_in_stage}d</span>
          {canMove && (
            // Non-drag alternative for moving cards (WCAG 2.2, 2.5.7 Dragging Movements).
            <select aria-label={`Move ${deal.name} to stage`} value={deal.stage.id}
              className="max-w-6 cursor-pointer appearance-none rounded border border-line bg-canvas px-1 text-center text-muted hover:border-brand focus:max-w-none focus:outline-2 focus:outline-brand"
              onChange={(e) => { const s = stages.find((x) => x.id === e.target.value); if (s) onMove(deal.id, s); }}>
              {stages.map((s) => <option key={s.id} value={s.id}>{s.id === deal.stage.id ? "⋯" : `Move to ${s.name}`}</option>)}
            </select>
          )}
        </span>
      </div>
    </article>
  );
}

function Column({ stage, stages, count, total, currency, deals, onDrop, canMove }: {
  stage: Stage; stages: Stage[]; count: number; total: number; currency: string; deals: Deal[];
  onDrop: (dealId: string, stage: Stage) => void; canMove: boolean;
}) {
  const [over, setOver] = useState(false);
  const accent = stage.kind === "won" ? "border-t-ok" : stage.kind === "lost" ? "border-t-danger" : "border-t-brand";
  return (
    <section aria-label={`Stage ${stage.name}`} data-stage={stage.name}
      onDragOver={(e) => { if (canMove) { e.preventDefault(); setOver(true); } }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData("text/deal-id"); if (id) onDrop(id, stage); }}
      className={`flex w-64 shrink-0 flex-col rounded-lg border border-t-4 ${accent} ${over ? "border-brand bg-brand-soft/60" : "border-line bg-canvas"}`}>
      <header className="px-3 pb-2 pt-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">{stage.name}</h2>
          <span className="rounded-full bg-surface px-2 text-xs text-muted">{count}</span>
        </div>
        <p className="text-xs text-muted">{money(total, currency)}{stage.kind === "open" && ` · ${stage.probability}%`}</p>
      </header>
      <div className="flex min-h-24 flex-1 flex-col gap-2 overflow-y-auto px-2 pb-3">
        {deals.map((d) => <DealCard key={d.id} deal={d} stages={stages} canMove={canMove} onMove={onDrop} />)}
        {deals.length === 0 && <p className="px-1 py-6 text-center text-xs text-muted">Drop deals here</p>}
      </div>
    </section>
  );
}

export function DealsPage() {
  const me = useMe();
  const perms = me.data?.permissions ?? [];
  const currency = me.data?.tenant.currency ?? "INR";
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "list" ? "list" : "board";
  const pipelines = usePipelines();
  const pipelineId = params.get("pipeline") ?? pipelines.data?.find((p) => p.is_default)?.id ?? null;
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 250); return () => clearTimeout(t); }, [search]);

  const board = useBoard(pipelineId, q);
  const list = useDeals({ q, pipeline_id: pipelineId ?? "" }, view === "list");
  const move = useMoveDeal(crmKeys.board(pipelineId, q));
  const [creating, setCreating] = useState(false);
  const [pendingLost, setPendingLost] = useState<Deal | null>(null);
  const [lostStage, setLostStage] = useState<Stage | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  const allDeals = useMemo(() => new Map((board.data?.columns ?? []).flatMap((c) => c.deals).map((d) => [d.id, d])), [board.data]);

  const onDrop = (dealId: string, stage: Stage) => {
    const deal = allDeals.get(dealId);
    if (!deal || deal.stage.id === stage.id) return;
    if (stage.kind === "lost") { setPendingLost(deal); setLostStage(stage); return; }
    setMoveError(null);
    move.mutate({ deal: { ...deal, stage: { ...stage }, status: stage.kind }, stageId: stage.id },
      { onError: (e) => setMoveError(e instanceof ApiError ? e.message : "Could not move the deal") });
  };

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader title="Deals" subtitle={board.data ? `${board.data.open_count} open · ${money(board.data.open_amount, currency)} pipeline · ${money(board.data.weighted_amount, currency)} weighted` : undefined}
        actions={<>
          {can(perms, "data:export") && <button type="button" className="btn-ghost" onClick={() => void downloadExport("deal")}><Icon name="download" />Export</button>}
          {can(perms, "deals:create") && <button type="button" className="btn-primary" onClick={() => setCreating(true)}><Icon name="plus" />New deal</button>}
        </>} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input type="search" className="input max-w-xs" placeholder="Search deals or accounts" aria-label="Search deals" value={search} onChange={(e) => setSearch(e.target.value)} />
        {(pipelines.data?.length ?? 0) > 1 && (
          <select className="input w-auto" aria-label="Pipeline" value={pipelineId ?? ""} onChange={(e) => setParam("pipeline", e.target.value)}>
            {pipelines.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <div className="ml-auto inline-flex rounded-md border border-line bg-surface p-0.5" role="tablist" aria-label="View">
          {(["board", "list"] as const).map((v) => (
            <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => setParam("view", v === "board" ? null : v)}
              className={`rounded px-3 py-1 text-sm font-medium ${view === v ? "bg-brand text-white" : "text-muted hover:text-ink"}`}>{v === "board" ? "Board" : "List"}</button>
          ))}
        </div>
      </div>
      {moveError && <div className="mb-3"><Alert>{moveError}</Alert></div>}
      {board.isLoading && <Spinner />}
      {board.error && <Alert>{board.error.message}</Alert>}

      {view === "board" && board.data && (
        <div className="-mx-4 overflow-x-auto px-4 pb-4 sm:-mx-6 sm:px-6">
          <div className="flex min-h-[60vh] gap-3">
            {board.data.columns.map((c) => (
              <Column key={c.stage.id} stage={c.stage} stages={board.data!.columns.map((x) => x.stage)} count={c.count} total={c.total_amount} currency={currency}
                deals={c.deals} onDrop={onDrop} canMove={can(perms, "deals:update")} />
            ))}
          </div>
        </div>
      )}

      {view === "list" && list.data && (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-canvas text-xs uppercase tracking-wide text-muted">
              <tr><th className="px-4 py-3">Deal</th><th className="px-4 py-3">Stage</th><th className="px-4 py-3 text-right">Value</th><th className="px-4 py-3">Close</th><th className="px-4 py-3">Owner</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody>
              {list.data.items.map((d) => (
                <tr key={d.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-canvas" onClick={() => navigate(`/deals/${d.id}`)}>
                  <td className="px-4 py-3"><Link to={`/deals/${d.id}`} className="font-medium hover:text-brand" onClick={(e) => e.stopPropagation()}>{d.name}</Link><span className="block text-xs text-muted">{d.account?.name}</span></td>
                  <td className="px-4 py-3">{d.stage.name}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-medium">{money(d.amount, d.currency)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted">{shortDate(d.expected_close_date)}</td>
                  <td className="whitespace-nowrap px-4 py-3">{d.owner?.full_name}</td>
                  <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                </tr>
              ))}
              {list.data.items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted">No deals match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <Modal title="New deal" onClose={() => setCreating(false)}>
          <DealForm onCancel={() => setCreating(false)} onSaved={(d) => { setCreating(false); navigate(`/deals/${d.id}`); }} />
        </Modal>
      )}
      {pendingLost && lostStage && (
        <LostReasonDialog dealName={pendingLost.name} onCancel={() => { setPendingLost(null); setLostStage(null); }}
          onConfirm={(reason) => {
            move.mutate({ deal: { ...pendingLost, stage: { ...lostStage }, status: "lost" }, stageId: lostStage.id, lostReason: reason },
              { onError: (e) => setMoveError(e instanceof ApiError ? e.message : "Could not move the deal") });
            setPendingLost(null); setLostStage(null);
          }} />
      )}
    </>
  );
}
